import { HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams, SideBarDef } from 'ag-grid-community';
import { tkGridOptions, tkSideBar } from 'src/app/shared/ag-grid/tk-grid';
import { NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { blobErrorMessage, filenameFromContentDisposition, openBlobInNewTab, saveBlob } from 'src/app/shared/utils/file-download.util';
import { DisbursementReportModalComponent } from '../disbursement-report-modal/disbursement-report-modal.component';
import { Subject, takeUntil } from 'rxjs';
import { FinancialsService } from 'src/app/services/modules/financials/financials.service';
import { DatePickerValue, DateRange } from 'src/app/components/date-picker/date-picker.component';
import { buildProviderFinancialsQuery, providerFinancialRows, providerFinancialTotal } from './provider-financials-grid';
import { formatDateRangeLabel } from './date-range-label';
import {
  FINANCIAL_COLUMNS,
  FINANCIAL_DEFAULT_COL_DEF,
  FINANCIAL_DEFAULT_SORT,
  FINANCIAL_PAGE_SIZE,
  FINANCIAL_STATUS_OPTIONS,
  FinancialRow,
  badgeTone,
  bookingMeta,
  financialGetRowId,
  financialRowId,
  formatMoney,
  formatPaymentStatus,
  formatStatus,
  isPaymentStatusClickable,
  paymentStatusTone,
} from './financial-grid';

type FinancialTab = 'sales' | 'disbursements' | 'settings';

@Component({
  selector: 'app-financial',
  templateUrl: './financial.component.html',
  styleUrls: [
    './financial.component.scss',
  ],
})
export class FinancialComponent implements OnDestroy {
  /** Column set for the placeholder tabs that have no data yet. */
  readonly placeholderColumns = [
    { key: 'booking', header: 'Booking ID' },
    { key: 'amount', header: 'Amount (Inc GST)', align: 'end' as const },
    { key: 'paidOut', header: 'Paid Out' },
    { key: 'status', header: 'Job Status' },
    { key: 'description', header: 'Description' },
    { key: 'customer', header: 'Customer Name' },
  ];

  /** Saved views are kept per type, so this grid's views stay apart from the old transactions grid. */
  readonly viewFilterType = 'provider-sales-invoices';
  readonly module = 'financials';
  /** No column here is pinned, so saved views must not force any to be. */
  readonly pinnedColumns: string[] = [];

  readonly statusOptions = FINANCIAL_STATUS_OPTIONS;
  readonly columnDefs: ColDef[] = FINANCIAL_COLUMNS;
  readonly defaultColDef: ColDef = FINANCIAL_DEFAULT_COL_DEF;
  readonly pageSizeOptions = [
    10,
    25,
    50,
    100,
    200,
  ];
  private readonly maxPageSize = 200;
  paginationPageSize = FINANCIAL_PAGE_SIZE;
  readonly sideBar: SideBarDef = tkSideBar('filters');
  readonly gridOptions: GridOptions = {
    // The payment status badges call this when clicked.
    context: {
      openDisbursementReport: (row: FinancialRow) => this.openDisbursementReport(row),
      downloadInvoice: (row: FinancialRow) => this.downloadInvoice(row),
      isInvoiceLoading: (bookingId: number) => this.invoiceLoading.has(bookingId),
    },
    // The API is paged, so rows are fetched one page at a time as the grid asks for them.
    ...tkGridOptions({
      rowModelType: 'infinite',
      pageSize: this.paginationPageSize,
      pageSizeOptions: this.pageSizeOptions,
      getRowId: financialGetRowId,
      sideBar: this.sideBar,
    }),
    suppressCellFocus: true,
    overlayNoRowsTemplate: '<span class="fin-empty">No sales invoices to show.</span>',
  };

  tab: FinancialTab = 'sales';
  searchText = '';
  /** What is typed in the search box, applied or not. It decides whether the clear button shows. */
  searchDraft = '';
  /** The statuses ticked in the toolbar dropdown: a mirror of the grid's Job Status set filter. */
  statusSelected: string[] = [];
  /**
   * The grid's Date column filter, mirrored for the toolbar button and picker. The grid owns the
   * filter; the toolbar and the floating filter / side bar are two ways of editing the same one.
   * Defaults to the current calendar month so the first listing request is scoped.
   */
  selectedRange: DateRange | null = currentMonthRange();
  /** True when the Date filter is something a range cannot show (e.g. "before" or "after"). */
  customDateFilter = false;
  gridApi?: GridApi;

  /** Current page of sales rows for the mobile card list (mirrors the grid page). */
  mobileRows: FinancialRow[] = [];
  mobileTotal = 0;
  mobilePage = 1;
  mobileLoading = false;

  private readonly destroy$ = new Subject<void>();
  /** Datatable requests still in flight; a replaced datasource's old request can finish after a new one starts. */
  private pendingRequests = 0;

  constructor(
    private financialsService: FinancialsService,
    private modalService: NgbModal,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  get mobilePageCount(): number {
    return Math.max(1, Math.ceil(this.mobileTotal / this.paginationPageSize) || 1);
  }

  trackCard = (_: number, row: FinancialRow): string => financialRowId(row);

  money(value: unknown): string {
    return formatMoney(value) || '—';
  }

  statusLabelFor(row: FinancialRow): string {
    return formatStatus(row.status);
  }

  statusTone(row: FinancialRow): string {
    return badgeTone(row.status);
  }

  paymentLabel(row: FinancialRow): string {
    return formatPaymentStatus(row.payment_status) || '—';
  }

  paymentTone(row: FinancialRow): string {
    return row.payment_status ? paymentStatusTone(row.payment_status) : 'muted';
  }

  canOpenReceipt(row: FinancialRow): boolean {
    return isPaymentStatusClickable(row);
  }

  isInvoiceBusy(bookingId: number): boolean {
    return this.invoiceLoading.has(bookingId);
  }

  /** "#15037" or the increase title when that is what the grid shows. */
  cardTitle(row: FinancialRow): string {
    const title = row.booking_title?.trim();
    if (title) {
      return title;
    }
    return row.booking_id == null ? '—' : `#${row.booking_id}`;
  }

  /** "Pallets · 12 Oct" under the booking id (Figma Data row); shared with desktop Booking ID cell. */
  cardMeta(row: FinancialRow): string {
    return bookingMeta(row);
  }

  goToMobilePage(page: number): void {
    if (!this.gridApi || this.gridApi.isDestroyed() || page < 1 || page > this.mobilePageCount) {
      return;
    }
    this.gridApi.paginationGoToPage(page - 1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True when the Date filter has something to clear, whether the toolbar can show it as a range or not. */
  get hasDateFilter(): boolean {
    return this.customDateFilter || !!this.selectedRange?.from;
  }

  /** "Status", the one picked label, or "Status (n)". */
  get statusLabel(): string {
    const picked = this.statusSelected;
    if (picked.length === 0) {
      return 'Status';
    }
    if (picked.length === 1) {
      return this.statusOptions.find((option) => option.value === picked[0])?.label ?? picked[0];
    }
    return `Status (${picked.length})`;
  }

  get dateLabel(): string {
    if (this.customDateFilter) {
      return 'Custom dates';
    }
    const range = this.selectedRange;
    return range?.from ? formatDateRangeLabel(range.from, range.to) : 'All dates';
  }

  selectTab(tab: FinancialTab): void {
    this.tab = tab;
    if (tab !== 'sales') {
      this.gridApi = undefined;
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.gridApi.setSideBarVisible(true);
    // Infinite model always paints 1 blank stub row before getRows returns — cover it now.
    this.setLoading(true);
    // Apply any selected range (default: current month) before the first fetch.
    const range = this.selectedRange;
    if (range?.from && range.to) {
      void this.gridApi
        .setColumnFilterModel('created_at', {
          filterType: 'date',
          type: 'inRange',
          dateFrom: toIsoDate(range.from),
          dateTo: toIsoDate(range.to),
        })
        .then(() => this.reload());
      return;
    }
    this.reload();
  }

  /** True while the CSV export is being fetched (it can take ~10 s for a large provider). */
  exporting = false;
  /** Bookings whose invoice PDF is being fetched; their row buttons show a spinner. */
  private readonly invoiceLoading = new Set<number>();

  /** Every row of the provider's financials as CSV. The grid's filters do not apply, so none are sent. */
  exportCsv(): void {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this.financialsService
      .exportProviderFinancials()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const filename = filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'financials_export.csv');
          if (response.body) {
            saveBlob(response.body, filename);
          }
          this.exporting = false;
        },
        error: async (error) => {
          this.exporting = false;
          AlertUtil.toast(await blobErrorMessage(error, 'The report could not be exported. Please try again.'), 'error');
        },
      });
  }

  /** Fetches the booking's invoice PDF with the token and opens it in a new tab (or saves it if the tab is blocked). */
  downloadInvoice(row: Pick<FinancialRow, 'booking_id'> | null | undefined): void {
    const bookingId = row?.booking_id;
    if (bookingId === null || bookingId === undefined || this.invoiceLoading.has(bookingId)) {
      return;
    }
    // AG Grid runs its click handlers outside Angular's zone; the request and its state belong inside it.
    this.ngZone.run(() => {
      this.setInvoiceLoading(bookingId, true);
      this.financialsService
        .getProviderInvoice(bookingId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: HttpResponse<Blob>) => {
            this.setInvoiceLoading(bookingId, false);
            const blob = response.body;
            if (blob && !openBlobInNewTab(blob)) {
              saveBlob(blob, `truckit-invoice-${bookingId}.pdf`);
            }
          },
          error: async (error) => {
            this.setInvoiceLoading(bookingId, false);
            AlertUtil.toast(await blobErrorMessage(error, 'The invoice could not be downloaded. Please try again.'), 'error');
          },
        });
    });
  }

  private setInvoiceLoading(bookingId: number, loading: boolean): void {
    if (loading) {
      this.invoiceLoading.add(bookingId);
    } else {
      this.invoiceLoading.delete(bookingId);
    }
    // Rows render the spinner from this state, so their Actions cells are redrawn.
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.refreshCells({
        columns: [
          'actions',
        ],
        force: true,
      });
    }
    // Mobile cards also read invoiceLoading; Set mutations need a CD nudge.
    this.cdr.markForCheck();
  }

  /** Opens the Disbursement Report for a row's Zai escrow item. */
  openDisbursementReport(row: Pick<FinancialRow, 'item_id' | 'booking_id'> | null | undefined): void {
    if (row?.item_id === null || row?.item_id === undefined) {
      return;
    }
    // AG Grid runs its click handlers outside Angular's zone. Opened from there, the modal (and the request it
    // starts) would not be rendered until something else triggered change detection, so it opens inside the zone.
    this.ngZone.run(() => {
      // The badge that was clicked keeps focus, and the modal hides the page behind it with aria-hidden;
      // a focused element must not sit under aria-hidden, so let go of focus first (the modal takes it).
      (document.activeElement as HTMLElement | null)?.blur();
      const modalRef = this.modalService.open(DisbursementReportModalComponent, { centered: true, size: 'lg' });
      modalRef.componentInstance.itemId = row.item_id;
      modalRef.componentInstance.bookingId = row.booking_id;
    });
  }

  /** "Clear filters" in the saved views control: the grid's filters are cleared there, the toolbar's status select is ours. */
  clearFiltersExternally(): void {
    this.searchText = '';
    this.searchDraft = '';
    this.statusSelected = [];
    this.gridApi?.setFilterModel(null);
  }

  /** Keeps the toolbar's search box and date button in step with the grid's filters. */
  onFilterChanged(): void {
    const filters = this.gridApi?.getFilterModel() ?? {};
    const description = filters['description'];
    this.searchText = description?.type === 'contains' && typeof description.filter === 'string' ? description.filter : '';
    this.searchDraft = this.searchText;
    const status = filters['status'];
    this.statusSelected = status?.filterType === 'set' && Array.isArray(status.values) ? status.values.map(String) : [];
    const model = filters['created_at'];
    this.customDateFilter = false;
    this.selectedRange = null;
    if (!model) {
      return;
    }
    const range = dateFilterToRange(model);
    if (range) {
      this.selectedRange = range;
    } else {
      this.customDateFilter = true;
    }
  }

  onPaginationChanged(): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    let newPageSize = this.gridApi.paginationGetPageSize();
    if (newPageSize > this.maxPageSize) {
      newPageSize = this.maxPageSize;
      this.gridApi.setGridOption('paginationPageSize', newPageSize);
    }
    if (newPageSize === this.paginationPageSize) {
      return;
    }
    this.paginationPageSize = newPageSize;
    this.gridApi.setGridOption('cacheBlockSize', newPageSize);
    this.reload();
  }

  /** The API cannot OR across columns, so search is the grid's own Description filter. */
  onSearch(query: string): void {
    this.searchText = query.trim();
    this.searchDraft = query;
    const model = this.searchText ? { filterType: 'text', type: 'contains', filter: this.searchText } : null;
    void this.gridApi?.setColumnFilterModel('description', model).then(() => this.gridApi?.onFilterChanged());
  }

  clearSearch(input: HTMLInputElement): void {
    input.value = '';
    this.onSearch('');
    input.focus();
  }

  /** Ticks or unticks a status. The dropdown edits the grid's own Job Status filter, which also re-queries the API. */
  toggleStatus(value: string): void {
    const picked = this.statusSelected.includes(value)
      ? this.statusSelected.filter((status) => status !== value)
      : [
          ...this.statusSelected,
          value,
        ];
    this.setStatuses(picked);
  }

  clearStatuses(): void {
    this.setStatuses([]);
  }

  private setStatuses(values: string[]): void {
    this.statusSelected = values;
    // No key at all when nothing is ticked.
    const model = values.length ? { filterType: 'set', values } : null;
    void this.gridApi?.setColumnFilterModel('status', model).then(() => this.gridApi?.onFilterChanged());
  }

  onDateApplied(value: DatePickerValue): void {
    if (!isDateRange(value) || !value.from) {
      this.onDateCleared();
      return;
    }
    // Local-time days, not toISOString (that shifts the day); the API includes the whole end day.
    this.setDateFilter(
      value.to
        ? { filterType: 'date', type: 'inRange', dateFrom: toIsoDate(value.from), dateTo: toIsoDate(value.to) }
        : { filterType: 'date', type: 'greaterThanOrEqual', dateFrom: toIsoDate(value.from) },
    );
  }

  onDateCleared(): void {
    this.setDateFilter(null);
  }

  /** Setting the grid's filter makes the infinite row model re-query by itself. */
  private setDateFilter(model: Record<string, unknown> | null): void {
    void this.gridApi?.setColumnFilterModel('created_at', model).then(() => this.gridApi?.onFilterChanged());
  }

  /** Throws away the loaded pages and fetches page 1 again with the current filters. */
  reload(): void {
    // A new datasource object makes the grid reset to the first page.
    this.gridApi?.setGridOption('datasource', this.createDatasource());
  }

  private createDatasource(): IDatasource {
    return {
      getRows: (params: IGetRowsParams) => {
        // The infinite row model shows nothing while a page loads, unlike My Jobs' server-side one,
        // so the grid's `loading` option is on for as long as any datatable request is pending.
        this.pendingRequests++;
        this.setLoading(true);
        const query = buildProviderFinancialsQuery({
          startRow: params.startRow,
          endRow: params.endRow,
          sortModel: params.sortModel?.length ? params.sortModel : FINANCIAL_DEFAULT_SORT,
          filterModel: requestFilterModel(params.filterModel),
        });

        this.financialsService
          .getProviderFinancialListing(query)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              const total = providerFinancialTotal(res);
              const rows = providerFinancialRows(res) as FinancialRow[];
              // Mobile cards mirror the same page the grid just received.
              this.mobileRows = rows;
              this.mobileTotal = total;
              this.mobilePage = Math.floor(params.startRow / this.paginationPageSize) + 1;
              params.successCallback(rows, total);
              if (--this.pendingRequests === 0) {
                this.showEmptyState(total === 0);
              }
              this.cdr.markForCheck();
            },
            error: () => {
              params.failCallback();
              if (--this.pendingRequests === 0) {
                this.setLoading(false);
              }
              this.cdr.markForCheck();
            },
          });
      },
    };
  }

  /** AG Grid's loading overlay is the `loading` grid option (showLoadingOverlay is deprecated since v32). */
  private setLoading(loading: boolean): void {
    this.mobileLoading = loading;
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.setGridOption('loading', loading);
    }
  }

  /** The infinite row model never switches to the "no rows" overlay by itself. */
  private showEmptyState(empty: boolean): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    // The loading overlay goes first, or it would still cover the "no rows" one.
    this.setLoading(false);
    if (empty) {
      const filtered = Object.keys(this.gridApi.getFilterModel() ?? {}).length > 0;
      this.gridApi.setGridOption('overlayNoRowsTemplate', `<span class="fin-empty">${filtered ? 'No transactions match these filters' : 'No sales invoices to show.'}</span>`);
      this.gridApi.showNoRowsOverlay();
    } else {
      this.gridApi.hideOverlay();
    }
  }
}

/** AG Grid's date filter keeps "YYYY-MM-DD 00:00:00"; the API wants the plain day. */
function requestFilterModel(gridModel: Record<string, any> | null | undefined): Record<string, any> {
  const model: Record<string, any> = { ...(gridModel ?? {}) };
  const created = model['created_at'];
  if (created?.filterType === 'date') {
    const day = (value: unknown) => (typeof value === 'string' ? value.slice(0, 10) : value);
    const { dateTo, ...rest } = created;
    model['created_at'] = { ...rest, dateFrom: day(created.dateFrom), ...(dateTo != null ? { dateTo: day(dateTo) } : {}) };
  }
  return model;
}

function isDateRange(value: DatePickerValue): value is DateRange {
  return !!value && 'from' in value;
}

function toIsoDate(date: NgbDateStruct): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

/** Inclusive first/last day of the calendar month containing `now` (local time). */
function currentMonthRange(now = new Date()): DateRange {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return {
    from: { year, month, day: 1 },
    to: { year, month, day: new Date(year, month, 0).getDate() },
  };
}

/** A date filter that is a plain from/to (or one day) can be shown on the toolbar; anything else cannot. */
function dateFilterToRange(model: any): DateRange | null {
  const parse = (value: unknown): NgbDateStruct | null => {
    const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    return match ? { year: +match[1], month: +match[2], day: +match[3] } : null;
  };
  const from = parse(model?.dateFrom);
  if (model?.type === 'equals' && from) {
    return { from, to: from };
  }
  const to = parse(model?.dateTo);
  if (model?.type === 'greaterThanOrEqual' && from) {
    return { from, to: null };
  }
  return model?.type === 'inRange' && from && to ? { from, to } : null;
}
